import { LoadingAnim } from '@assets/component/Loading/LoadingAnim';
import { ModalEditNarrative } from '@assets/component/ModalEditNarrative/ModalEditNarrative';
import { ModalPickPromptDemo } from '@assets/component/ModalPickPromptDemo/ModalPickPromptDemo';
import {
    ScrollPanel,
    ScrollPanelStore
} from '@assets/component/Panel/ScrollPanel/ScrollPanel';
import SideChat from '@assets/component/SideChat/SideChat';
import { Split } from '@assets/component/Split/Split';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import {
    FactsTable,
    NarrativeItem,
    NarrativeStore
} from '@assets/model/NarrativeStore';
import { useStore, useStoreUI } from '@assets/model/Store';
import {
    dragAndDropFeature,
    hotkeysCoreFeature,
    ItemInstance,
    selectionFeature,
    syncDataLoaderFeature
} from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { default as classNames, default as cn } from 'classnames';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { StrictMode, useEffect, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import * as style from './DealNarrative.module.less';
import noOutputsAnimation from './no_outputs_animation.webm';
import { formatDivMarkup } from '@assets/utils/CompUtils';
import NarrativeElement from '@assets/component/NarrativeElement/NarrativeElement';

const DealNarrativePage = observer(() => {
    const store = useStore();
    const ui = useStoreUI();

    const [narStore, setNarrativeStore] = useState<NarrativeStore>(null);
    const [rebuildTreeTrigger, setRebuildTreeTrigger] = useState(0);

    const showFactProcessingScan = narStore?.isBackendWorking ?? false;

    const [detailDocNameSelected, setDetailDocName] = useState(null);
    const [contentScrollApi] = useState(() => new ScrollPanelStore());

    const narrativeItems: NarrativeItem[] = narStore?.narrativeTable || [];
    const factsTable: FactsTable = narStore?.factsTable || null;

    useEffect(() => {
        if (narStore) {
            narStore.unregister();
        }

        setNarrativeStore(new NarrativeStore(store.selectedReportId));
    }, [store.selectedReportId]);

    useEffect(() => {
        tree.rebuildTree();
    }, [narrativeItems, rebuildTreeTrigger]);

    useEffect(() => {
        if (detailDocNameSelected == null) {
            if (narrativeItems.length > 0) {
                const itemId = narrativeItems[0].itemId;
                setDetailDocName(itemId);
                setAnchorByItemId(itemId);
            }
        }
    }, [detailDocNameSelected, narrativeItems]);

    const setAnchorByItemId = itemId => {
        if (itemId) {
            const full_name = 'narrative_anchor_' + itemId;
            contentScrollApi.eventScrollToAnchor = full_name;
        }
    };

    const find_assertion_by_id = (id: string) => {
        return narStore.getNarrativeItemById(id);
    };

    //TODO: Need to fix since moving to MobX
    const EXPERIMENTAL_DRAG_N_DROP = false;
    const tree = useTree<NarrativeItem>({
        initialState: narrativeItems as any,
        rootItemId: 'root',
        canReorder: EXPERIMENTAL_DRAG_N_DROP,
        // canDrag: items => true,
        // canDrop: (items, { item }) => {
        //     // //console.log('Drag Target ', item.getId());
        //     // const dragId = items[0].getId();
        //     // const targeteId = item.getId();
        //     // if (dragId.indexOf(':') == -1) {
        //     //     return targeteId.indexOf(':') == -1;
        //     // } else {
        //     //     const parentId = dragId.substring(0, dragId.lastIndexOf(':'));
        //     //     //console.log('Drag Item Parent', dragId);
        //     //     return parentId == targeteId;
        //     // }
        // },
        // onDrop: (items, target) => {
        //     // if ('insertionIndex' in target) {
        //     //     const dragItem = items[0].getItemData();
        //     //     const dragContainer =
        //     //         target.item.getId() == 'root'
        //     //             ? narrativeTree.narrative_items
        //     //             : target.item.getItemData().sub_items; //This is a parent item
        //     //     const dropSwitchWith = dragContainer.find(
        //     //         e => e.displayIndex == target.insertionIndex
        //     //     );
        //     //     const dragItemDisplayIndex = dragItem.displayIndex;
        //     //     dragItem.displayIndex = dropSwitchWith.displayIndex;
        //     //     dropSwitchWith.displayIndex = dragItemDisplayIndex;
        //     //     const narrativeCopy = _.cloneDeep(narrativeTree);
        //     //     setRebuildTreeTrigger((rebuildTreeTrigger + 1) % 100);
        //     //     //setNarrativeTree(narrativeCopy);
        //     // }
        // },
        getItemName: item => {
            const item_box = find_assertion_by_id(item.getId());
            if (!item_box) {
                return 'Unknown';
            }
            return item_box.ui_display;
        },
        isItemFolder: item => {
            const item_box = find_assertion_by_id(item.getId());
            if (!item_box) {
                return false;
            }

            return item_box.sub_items && item_box.sub_items.length > 0;
        },
        dataLoader: {
            getItem: itemId => {
                if (!isDataLoaded()) return null;

                const itemData = find_assertion_by_id(itemId);
                if (!itemData) {
                    return {} as NarrativeItem;
                }

                return itemData;
            },

            getChildren: specificItem => {
                if (!isDataLoaded()) return [];
                const sectionData = find_assertion_by_id(specificItem);
                if (sectionData && sectionData.sub_items) {
                    return sectionData.sub_items.map(
                        (_, index) => sectionData.sub_items[index].itemId
                    );
                } else {
                    return [];
                }
            }
        },
        onPrimaryAction: item => {
            setDetailDocName(item.getItemData().rootDocId);
            setAnchorByItemId(item.getItemData().itemId);
        },

        indent: 20,
        features: [
            syncDataLoaderFeature,
            selectionFeature,
            hotkeysCoreFeature,
            dragAndDropFeature
        ]
    });

    const isDataLoaded = () => {
        const isNotReady = !narStore || !narrativeItems;
        return !isNotReady;
    };

    if (!isDataLoaded()) {
        return <div>Loading assertions...</div>;
    }

    const isNarrativeEmpty = narrativeItems.length === 0;

    const getFactCountSum = (narrItem: NarrativeItem) => {
        const get_facts_recursive = (
            currItem: NarrativeItem,
            track_recursion: number = 0
        ) => {
            if (track_recursion > 5) {
                return -1000;
            }

            if (currItem.level == 'L3') {
                const list =
                    factsTable[currItem.itemId]?.supporting_facts ?? [];
                return list.filter(f => !f.hidden).length;
            } else {
                let totalFacts = 0;
                for (let childNarItem of currItem?.sub_items ?? []) {
                    totalFacts += get_facts_recursive(
                        childNarItem,
                        track_recursion + 1
                    );
                }
                return totalFacts;
            }
        };

        return get_facts_recursive(narrItem);
    };

    // const getFactCountSum = (itemId: string) => {
    //     //Can be a lot cleanier
    //     let totalFacts = 0;
    //     let factId = itemId.replaceAll('.', ':');
    //     if (factId.indexOf(':') == -1) {
    //         //Root category
    //         for (let key in factsTable) {
    //             if (key.split(':')[0] == factId) {
    //                 totalFacts += factsTable[key].supporting_facts.filter(
    //                     x => x.hidden != true
    //                 ).length;
    //             }
    //         }
    //     } else if (factId.split(':').length == 2) {
    //         let filterKey = factId + ':';
    //         for (let key in factsTable) {
    //             if (key.indexOf(filterKey) == 0) {
    //                 totalFacts += factsTable[key].supporting_facts.filter(
    //                     x => x.hidden != true
    //                 ).length;
    //             }
    //         }
    //     } else {
    //         if (factsTable && factsTable.hasOwnProperty(factId)) {
    //             totalFacts = factsTable[factId].supporting_facts.filter(
    //                 x => x.hidden != true
    //             ).length;
    //         }
    //     }

    //     return totalFacts;
    // };

    //render
    const renderEmptyState = () => (
        <div className={style.emptyState}>
            <video
                className={style.emptyStateVideo}
                src={noOutputsAnimation}
                autoPlay
                loop
                muted
                playsInline
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                onContextMenu={e => e.preventDefault()}
            />
            <div className={style.emptyStateText}>No outline generated yet</div>
        </div>
    );
    const renderSelectedNode = () => {
        if (isNarrativeEmpty) {
            if (showFactProcessingScan) {
                return null;
            }
            return renderEmptyState();
        }

        const selectedInfo = find_assertion_by_id(detailDocNameSelected);
        if (!selectedInfo) {
            return <div>No assertion selected</div>;
        }

        const scan_button = (
            <Button
                variant="light"
                className={style.scanButton}
                onClick={() => {
                    onScanForFacts();
                }}
            >
                <i className="bi bi-search"></i>
                Scan
            </Button>
        );

        return (
            <Flex
                fillCell={true}
                justify="center"
                alignItems="center"
                style={{
                    borderRadius: '0 20px 0 0',
                    backgroundColor: 'white'
                }}
            >
                <div className={style.selectedNarrativeItemContainer}>
                    <ScrollPanel
                        api={contentScrollApi}
                        style={{
                            backgroundColor: '#F9FAFB',
                            borderRadius: '12px'
                        }}
                    >
                        <NarrativeElement
                            item={selectedInfo}
                            narStore={narStore}
                            key={selectedInfo.itemId}
                        />
                    </ScrollPanel>
                </div>
            </Flex>
        );
    };

    const renderTreeItem = (item: ItemInstance<NarrativeItem>) => {
        const itemId = item.getId();
        let itemIdDisplay = item.getItemData().ui_displayIdGenerated;

        let itemLevel = item.getItemData().level;

        let totalFacts = getFactCountSum(item.getItemData());

        return (
            <button
                {...item.getProps()}
                key={item.getId()}
                data-nar-item-level={itemLevel}
                className={style.treeItemContainer}
                style={{
                    paddingLeft: `${item.getItemMeta().level * 10}px`
                }}
            >
                <div
                    data-nar-item-level={itemLevel}
                    className={cn(
                        style.treeitem,
                        { [style.focused]: item.isFocused() },
                        { [style.expanded]: item.isExpanded() },
                        { [style.selected]: item.isSelected() },
                        {
                            [style.selected_chat_context]:
                                item.getId() == ui.sideChatSelectedContext
                        },
                        { [style.folder]: item.isFolder() }
                    )}
                >
                    <Flex
                        className={style.treeItemFlex}
                        direction="row"
                        wrap="nowrap"
                        inline={true}
                    >
                        <div className={style.item_id}>{itemIdDisplay}</div>
                        <div className={style.item_name}>
                            {item.getItemName()}
                        </div>
                        {totalFacts > 0 && (
                            <div className={style.fact_total}>{totalFacts}</div>
                        )}
                    </Flex>
                </div>
            </button>
        );
    };

    //callbacks
    const onGenerateA1 = () => {
        const fb = getFirebase();
        if (confirm('Are you sure?')) {
            fb.flowtask_add_a1_generation_task(store.selectedReportId);
        }
    };

    const onScanForFacts = () => {
        const ui = useStoreUI();
        const add_scan_job = (filename: string) => {
            const fb = getFirebase();
            if (narStore) {
                narStore.isBackendWorking = true;
            }

            fb.flowtask_add_scan_for_facts(
                store.selectedReportId,
                filename,
                detailDocNameSelected
            );
            ui.modal = null;
        };

        ui.modal = {
            title: 'Pick File',
            el: (
                <ModalPickPromptDemo
                    onSelectFile={(f: string) => {
                        add_scan_job(f);
                    }}
                />
            )
        };
    };

    return (
        <StrictMode>
            <Flex fillCell={true}>
                <SideChat narStore={narStore} />
                <Split layoutPercent={[25, 75]}>
                    <Flex
                        direction="column"
                        className={style.narrTreeFlex}
                        fillCell={true}
                    >
                        <Flex
                            className={style.back_to_workstreams}
                            direction="row"
                        >
                            <div
                                style={{
                                    flexGrow: 1,
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    paddingLeft: '15px'
                                }}
                            >
                                A1 Narrative
                            </div>
                            <Dropdown>
                                <Dropdown.Toggle className="dropdown-toggle-overide">
                                    <i
                                        className={classNames(
                                            'bi bi-three-dots-vertical'
                                        )}
                                        style={{
                                            color: 'white',
                                            fontSize: '14px',
                                            position: 'relative'
                                        }}
                                    ></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item>
                                        <div
                                            onClick={() => {
                                                onGenerateA1();
                                            }}
                                        >
                                            Generate A1
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Flex>
                        <ScrollPanel enableHideScrollUntilMouseOver={true}>
                            {isNarrativeEmpty && (
                                <div className={style.treeEmptyText}>
                                    No items to show yet
                                </div>
                            )}
                            <div
                                {...tree.getContainerProps()}
                                className={style.tree}
                                data-next-action-danger={
                                    ui.sideChatIsNextActionChangingData
                                }
                            >
                                {tree.getItems().map(item => {
                                    return renderTreeItem(item);
                                })}
                                <div
                                    style={tree.getDragLineStyle()}
                                    className="dragline"
                                />
                            </div>
                        </ScrollPanel>
                    </Flex>

                    <Flex
                        fillCell={true}
                        style={{
                            borderRadius: '0 20px 0 0'
                        }}
                    >
                        <div
                            style={{
                                left: '45%',
                                top: '0%',
                                position: 'absolute',
                                zIndex: 1000,
                                background:
                                    'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 20%)',
                                visibility: showFactProcessingScan
                                    ? 'visible'
                                    : 'hidden'
                            }}
                        >
                            <LoadingAnim />
                        </div>

                        {renderSelectedNode()}
                    </Flex>
                </Split>
            </Flex>
        </StrictMode>
    );
});

export default DealNarrativePage;
